import { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';

import {
  Collapsible,
  CollapsiblePanel,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { CopyButton } from '@/shared/components/CopyButton';
import { RetryButton } from '@/shared/components/RetryButton';
import { useExecutionTranscript } from '../hooks/useExecutionTranscript';

export interface RunPromptProps {
  workspaceId: string;
  taskId: string;
  executionId: string;
}

/**
 * The prompt a run was given (spec 149 US2), collapsed until asked for.
 *
 * This reverses UI spec 067 FR-010, which kept it out of the drawer. The
 * reason given there — that a prompt shown beside a run could only be the
 * workflow's current wording — never held: the run stores the prompt it was
 * rendered with (`prompt_snapshot`), and that is what this shows. A
 * conversation about a run is half blind without it.
 *
 * Read from the transcript's own read, which already carries it, and only once
 * opened. The prompt is fixed when the run is created, so this read never asks
 * to be refreshed for it; the transcript below it polls on its own terms.
 */
export function RunPrompt({
  workspaceId,
  taskId,
  executionId,
}: RunPromptProps) {
  const [open, setOpen] = useState(false);
  const { promptSnapshot, isPending, loadFailed, retry, isFetching } =
    useExecutionTranscript(workspaceId, taskId, executionId, {
      enabled: open,
      isFinished: true,
    });

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="text-muted-foreground hover:text-foreground group flex items-center gap-1.5 text-xs font-medium">
        <ChevronDownIcon
          aria-hidden="true"
          className="size-3.5 shrink-0 -rotate-90 transition-transform group-data-open:rotate-0"
        />
        Prompt
      </CollapsibleTrigger>
      <CollapsiblePanel className="pt-2">
        {isPending ? (
          <p className="text-muted-foreground text-sm">Loading prompt…</p>
        ) : loadFailed && promptSnapshot === undefined ? (
          <div className="flex flex-col items-start gap-2">
            <p className="text-muted-foreground text-sm">
              The prompt could not be loaded.
            </p>
            <RetryButton onRetry={retry} isFetching={isFetching} />
          </div>
        ) : promptSnapshot ? (
          <div className="relative">
            {/* As rendered for the agent, never as markdown: what it was told,
                character for character. Bounded, so a long prompt scrolls in
                place rather than pushing the transcript out of reach. */}
            <pre
              data-testid="run-prompt"
              className="bg-muted text-foreground max-h-80 overflow-auto rounded-md p-3 pe-10 font-mono text-xs leading-relaxed break-words whitespace-pre-wrap"
            >
              {promptSnapshot}
            </pre>
            <CopyButton
              text={promptSnapshot}
              label="Copy prompt"
              className="absolute top-1 right-1"
            />
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">
            This run recorded no prompt.
          </p>
        )}
      </CollapsiblePanel>
    </Collapsible>
  );
}
